import { Tables } from '../../../database.types';
import { Button } from "@/components/ui/button";
import { BrainIcon, ExternalLink, Check } from "lucide-react";
import { Link } from 'react-router-dom';
import fieldMetadata from '../../lib/fieldMetadata.json';
import { cn } from "@/lib/utils";

type Brand = Tables<'brands'>;

interface BrandProgressProps {
  brand: Brand | null;
  showLink?: boolean;
  className?: string;
}

const AnimatedCheckmark = () => (
  <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full">
    <Check className="h-6 w-6 text-green-600" />
  </div>
);

export function BrandProgress({ brand, showLink = true, className = "" }: BrandProgressProps) {
  const calculateProgress = () => {
    if (!brand) return 0;
    const visibleFields = Object.keys(fieldMetadata).filter(key => {
      const metadata = fieldMetadata[key as keyof typeof fieldMetadata];
      return !metadata?.hidden && key !== 'status';
    });
    const totalFields = visibleFields.length;
    const filledFields = visibleFields.filter(key => {
      const value = brand[key as keyof Brand];
      return value !== null && value !== undefined && value !== '';
    }).length;
    return (filledFields / totalFields) * 100;
  };

  const progress = calculateProgress();

  const getPhase = (progress: number) => {
    if (progress < 20) return { name: 'Weak', color: 'text-red-600' };
    if (progress < 50) return { name: 'Ok', color: 'text-yellow-600' };
    if (progress < 80) return { name: 'Getting there', color: 'text-blue-600' };
    return { name: 'Strong', color: 'text-green-600' };
  };

  const currentPhase = getPhase(progress);

  const phases = [
    { name: 'Weak', start: 0, end: 20, color: 'bg-red-500' },
    { name: 'Ok', start: 20, end: 50, color: 'bg-yellow-500' },
    { name: 'Getting there', start: 50, end: 80, color: 'bg-blue-500' },
    { name: 'Strong', start: 80, end: 100, color: 'bg-green-500' }
  ];

  return (
    <div className={cn(`flex items-start gap-4 bg-muted p-6 rounded-lg`, className)}>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          {showLink && (
            <Button
              asChild
              variant="outline"
              size="sm"
            >
              <Link 
                to="/brands/genius"
                className="flex items-center gap-2"
              >
                <BrainIcon className="h-4 w-4" />
                <span>Open Brand Genius</span>
                <ExternalLink className="h-3 w-3" />
              </Link>
            </Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          The more complete your brand profile, the better your brand kit and AI content will be. Complete all fields in Brand Genius for optimal results.
        </p>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex-1 h-3 bg-muted-foreground/20 rounded-full overflow-hidden relative">
              <div
                className={`h-full transition-all duration-500 ${
                  progress < 20 ? 'bg-red-500' :
                  progress < 50 ? 'bg-yellow-500' :
                  progress < 80 ? 'bg-blue-500' :
                  'bg-green-500'
                }`}
                style={{ width: `${progress}%` }}
              />
            </div>
            {progress === 100 && <AnimatedCheckmark />}
          </div>
          
          {/* Phase Scale */}
          <div className="relative">
            <div className="flex justify-between relative">
              {phases.map((phase, index) => (
                <div key={phase.name} className="flex flex-col items-center flex-1">
                  {/* Phase marker */}
                  <div className="relative w-full flex justify-center">
                    <div 
                      className={`w-3 h-3 rounded-full border-2 border-background shadow-sm transition-all duration-300 ${
                        progress >= phase.start ? phase.color : 'bg-muted-foreground/30'
                      }`}
                    />
                    {/* Connection line */}
                    {index < phases.length - 1 && (
                      <div className="absolute top-1.5 left-1/2 w-full h-0.5 bg-muted-foreground/20 -z-10" />
                    )}
                  </div>
                  {/* Phase label */}
                  <span className={`text-xs font-medium mt-2 transition-colors duration-300 ${
                    progress >= phase.start ? currentPhase.color : 'text-muted-foreground/60'
                  }`}>
                    {phase.name}
                  </span>
                  {/* Progress threshold */}
                  <span className="text-xs text-muted-foreground/50 mt-0.5">
                    {phase.start}%{index === phases.length - 1 ? '+' : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {progress < 100 && (
          <p className="text-xs text-muted-foreground">
            {progress < 20 && "Your brand needs significant completion to generate quality content."}
            {progress >= 20 && progress < 50 && "Your brand is getting started. Add more details for better results."}
            {progress >= 50 && progress < 80 && "Your brand is progressing well. Keep filling in fields to reach Strong status."}
            {progress >= 80 && progress < 100 && "Your brand is strong! Complete the remaining fields for optimal performance."}
          </p>
        )}
      </div>
    </div>
  );
} 